// ============================================================================
// L'ONGLET SOLDES — l'arbre de la maquette (`prmT2`), relevé élément par élément (2026-08-29, puis
// la LIVRAISON EXCLUSIONS du 2026-08-30) : cartes de saison (label + règle, bord 1,5 px — les dates
// vivent dans le panneau), panneau de la saison (en-tête dates + pastille, grille des démarques,
// note légale grise), carte « Exclusions de campagne » (→ `soldes-exclusions.tsx`), carte « Aperçu
// des démarques » (référence BARRÉE, dernière démarque en teal), carte « Exclus des soldes » (TROIS
// familles de motifs colorées), et la BARRE D'ACTIVATION en pied (3 états exclusifs).
//
// ⚠️ LES DATES NE SE SAISISSENT PAS : calculées serveur par la règle nationale. Le bouton
// « Corriger » (passe du 2026-08-29) pose des DATES D'ARRÊTÉ quand un décret ou une dérogation les
// déplace — la maquette du 30/08 ne le porte pas (elle a été dessinée depuis le brief exclusions,
// avant cette passe) : on le GARDE, c'est une capacité validée, et la note grise de la carte le dit.
// La répartition « à partir du … » vient AUSSI du serveur — la même fonction que l'écriture, jamais
// une formule recopiée ici.
// ⚠️ « Éteindre les soldes » n'est pas dans la maquette (prototype sans écriture) : ajout fonctionnel,
// posé sobrement dans la barre d'activation.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { confirm, DataTable, showToast, Trans, type ColumnDef } from '@orgazone/ui-web';
import { formatMoney } from '@orgazone/money-format';
import { api, type ApercuSolde, type ExclusionSoldes, type SoldesExcludedRow, type SoldesState } from '../api';
import { CarteExclusions, sxDimLabel } from './soldes-exclusions';
import { key, t } from '../i18n';

const euro = (cents: number) => formatMoney(cents, { thousandsSep: ' ' });
const day = (ms: number) => new Date(ms).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
/** Date CIVILE Paris (AAAA-MM-JJ) d'un epoch — `fr-CA` donne l'ISO ; minuit Paris = veille en UTC. */
const jourCivilParis = (ms: number) => new Date(ms).toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' });

/** Les trois démarques classiques — le point de départ, pas une contrainte (1 à 4, croissantes). */
const MARKS_DEFAUT = [30, 50, 70];
/**
 * LE RANG D'UNE DÉMARQUE — résolu au RENDU, jamais écrit en dur.
 *
 * 🔴 IL ÉTAIT UNE TABLE FRANÇAISE QU'AUCUNE RÈGLE NE VOYAIT : `1ʳᵉ`, `2ᵉ`… sont trop courts et
 *    trop peu alphabétiques pour qu'`isHumanText` les reconnaisse, donc l'audit ne les comptait
 *    pas — l'écran aurait affiché des ordinaux FRANÇAIS à un compte anglais, sous un « 0 en dur ».
 *
 * ⚠️ LES TROIS PREMIERS SONT DES CLÉS À PART, le reste un MOTIF. L'anglais dit `1st`, `2nd`, `3rd`
 *    puis `th` pour tout le reste ; le français `1ʳᵉ` puis `ᵉ`. Trois exceptions et une règle :
 *    c'est exactement ce que `Intl.PluralRules({ type: 'ordinal' })` encode, et notre moteur ne
 *    lit que les catégories CARDINALES — on écrit donc les trois cas, plutôt que de prétendre
 *    dériver ce qu'on ne sait pas dériver.
 */
const ORDINAUX = [key('1ʳᵉ'), key('2ᵉ'), key('3ᵉ')] as const;
const ordinal = (i: number): string => {
  const exact = ORDINAUX[i];
  return exact === undefined ? t('{n}ᵉ', { n: i + 1 }) : t(exact);
};

// Les micro-styles récurrents de la maquette, nommés une fois.
const MONO_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
  textTransform: 'uppercase', color: 'var(--ink-500)',
};
const CARTE: React.CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
};
const PASTILLE_GRISE: React.CSSProperties = {
  height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)',
  color: 'var(--ink-600)', fontSize: 'var(--t-note)', fontWeight: 600, display: 'inline-flex',
  alignItems: 'center', flexShrink: 0,
};
const NOTE_GRISE: React.CSSProperties = {
  marginTop: 13, padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)',
  border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6,
};

/**
 * Les colonnes de l'APERÇU. ⚠️ Bâties depuis les % SAISIS : le nombre de démarques est réglable
 * (1 à 4), donc les colonnes le sont aussi — une liste figée afficherait « 3ᵉ » sur une campagne
 * qui n'en compte que deux.
 * ⚠️ La DERNIÈRE démarque en teal : c'est le prix de fin de soldes, celui qu'on lit d'abord.
 */
function colonnesDeLApercu(pcts: number[]): ColumnDef<ApercuSolde>[] {
  return [
    {
      key: 'article', label: t('Article'), minWidth: 220,
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 'var(--r-r2)', background: 'var(--ink-300)', flexShrink: 0 }} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
            <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>{r.code}</span>
          </span>
        </span>
      ),
    },
    {
      key: 'ref', label: t('Référence'), align: 'end', numeric: true, width: 110,
      // La référence est BARRÉE — c'est le prix de comparaison légal, plus le prix payé.
      render: (r) => (
        <span
          title={r.refDifferente ? t('Le prix le plus bas des 30 derniers jours diffère du prix actuel — c’est LUI la référence légale.') : undefined}
          style={{ color: 'var(--ink-500)', textDecoration: 'line-through' }}
        >{euro(r.refCents)}</span>
      ),
    },
    ...pcts.map((pct, i): ColumnDef<ApercuSolde> => ({
      key: `m${i}`,
      label: t('{rang} (−{pct} %)', { rang: ordinal(i), pct }),
      align: 'end', numeric: true, width: 104,
      // ⚠️ LES MONTANTS VIENNENT DU SERVEUR (`markedCents`), plus d'un second calcul à l'écran : il
      //    refaisait ici l'arithmétique déjà faite là-bas, et deux copies d'un calcul d'argent
      //    finissent par ne plus dire la même chose.
      // ⚠️ Un TIRET, jamais « 0,00 € », quand la valeur manque : les colonnes suivent les % TAPÉS
      //    (immédiat) et les montants la réponse serveur (débouncée) — pendant cet instant, un zéro
      //    se lirait comme un prix réel (§23.0).
      render: (r) => (
        <span style={{
          fontWeight: i === pcts.length - 1 ? 700 : 600,
          color: i === pcts.length - 1 ? 'var(--teal-800)' : 'var(--ink-800)',
        }}>{r.markedCents[i] == null ? <span style={{ color: 'var(--ink-400)' }}>—</span> : euro(r.markedCents[i]!)}</span>
      ),
    })),
  ];
}

/**
 * LE MOTIF D'UN EXCLU, tel qu'il se lit — « Exclu de campagne — Millésime ».
 * ⚠️ LE SERVEUR ENVOIE LE FAIT ET LA CLÉ DE DIMENSION, JAMAIS LA PHRASE : nommer la dimension avec le mot
 *    du magasin est déjà le travail de `sxDimLabel` (le panneau d'exclusions) — une phrase composée au
 *    serveur écrirait la même règle une seconde fois, et les deux diraient un jour deux mots différents
 *    pour la même dimension, sur le même écran.
 * ⚠️ `dim` n'accompagne qu'une exclusion de CAMPAGNE : un drapeau de fiche ou un prix libre n'ont pas
 *    de dimension, leur motif se lit tel quel — et une dimension que le serveur poserait sur l'un d'eux
 *    serait ignorée plutôt qu'affichée à contresens.
 */
export function exclusionReason(r: SoldesExcludedRow): string {
  return r.kind === 'campagne' && r.dim ? `${r.why} — ${sxDimLabel(r.dim)}` : r.why;
}

/**
 * Les colonnes des EXCLUS. ⚠️ La COULEUR du motif dit la famille : campagne (vos choix) en ambre,
 * prix libre en violet, drapeau de fiche en gris — les confondre ferait croire à une seule règle.
 */
// ⚠️ UNE FONCTION, APPELÉE AU RENDU : le tableau partagé affiche son intitulé tel quel, et une constante de module
//    figerait une clé française dans l'en-tête de tous les comptes.
const colonnesExclus = (): ColumnDef<SoldesExcludedRow>[] => [
  { key: 'nom', label: t('Article'), minWidth: 200, render: (r) => <span style={{ fontWeight: 600, color: 'var(--ink-900)' }}>{r.name}</span> },
  { key: 'code', label: t('Code'), mono: true, width: 140, render: (r) => r.code },
  {
    key: 'motif', label: t('Motif'), width: 210,
    render: (r) => {
      const ton = r.kind === 'campagne'
        ? { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' }
        : r.kind === 'prix_libre'
          ? { bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' }
          : { bg: 'var(--ink-100)', fg: 'var(--ink-600)' };
      return (
        <span style={{
          height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: ton.bg, color: ton.fg,
          fontSize: 'var(--t-meta)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
        }}>{exclusionReason(r)}</span>
      );
    },
  },
];

export function SoldesTab() {
  const [season, setSeason] = useState<'hiver' | 'ete'>('hiver');
  /** Les % en FRAPPE BRUTE — parsés à la dérivation, comme partout. */
  const [marksBruts, setMarksBruts] = useState<string[]>(MARKS_DEFAUT.map(String));
  const [etat, setEtat] = useState<SoldesState | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [ecriture, setEcriture] = useState(false);
  /** L'éditeur des dates d'arrêté — replié par défaut ; les dates en FRAPPE BRUTE (AAAA-MM-JJ). */
  const [editionDates, setEditionDates] = useState(false);
  const [datesBrutes, setDatesBrutes] = useState({ start: '', end: '' });
  /**
   * Les exclusions EN COURS D'ÉDITION. `null` = on n'y a pas touché → on suit ce que le serveur
   * résout (les exclusions de la campagne écrite). Dès la première coche, l'écran a son propre état,
   * et c'est LUI qui part au serveur pour recalculer les comptes.
   */
  const [exclEdit, setExclEdit] = useState<ExclusionSoldes[] | null>(null);

  // Les % exploitables : entiers 1..90, STRICTEMENT croissants — la même règle que le serveur, qui
  // reste l'arbitre (il refuse nommément ce que l'écran aurait laissé passer).
  const marks = useMemo(() => {
    const nets = marksBruts.map((v) => Number.parseInt(v.trim(), 10));
    if (nets.some((n) => !Number.isInteger(n) || n < 1 || n > 90)) return null;
    for (let i = 1; i < nets.length; i += 1) if (nets[i]! <= nets[i - 1]!) return null;
    return nets;
  }, [marksBruts]);

  /** Un compteur qu'on incrémente pour REDEMANDER l'état (bouton « Réessayer » après un échec). */
  const [relance, setRelance] = useState(0);

  useEffect(() => {
    let vivant = true;
    const timer = setTimeout(() => {
      // ⚠️ Les exclusions en cours d'édition partent AU SERVEUR : c'est lui qui mesure les écartés
      //    et filtre l'aperçu. L'écran ne recalcule jamais un périmètre de son côté (§23.0).
      api.soldesState(season, marks ?? MARKS_DEFAUT, exclEdit ?? undefined)
        .then((r) => { if (vivant) { setEtat(r); setFailure(null); } })
        // ⚠️ L'état PRÉCÉDENT est conservé (pas de `setEtat(null)`) : la carte dit l'échec et que
        //    les comptes affichés datent d'avant — vider l'écran ferait disparaître le contexte.
        .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    }, 300);
    return () => { vivant = false; clearTimeout(timer); };
  }, [season, marks, exclEdit, relance]);

  const fenetre = etat?.saisons.find((f) => f.saison === season) ?? null;
  const campagne = etat?.campagne ?? null;
  const dejaCetteSaison = campagne !== null && campagne.active
    && fenetre !== null && campagne.saison === fenetre.saison && campagne.year === fenetre.year;

  /** Les exclusions AFFICHÉES : celles en cours d'édition, sinon celles que le serveur a résolues.
   *  ⚠️ Mémoïsé : ce tableau est une dépendance de `dirty` — recréé à chaque rendu, il en annulerait
   *  la mémoïsation (react-hooks/exhaustive-deps, CI --max-warnings 0). */
  const exclusions = useMemo(() => exclEdit ?? etat?.exclusions ?? [], [exclEdit, etat]);

  /**
   * La campagne active porte-t-elle autre chose que ce qui est à l'écran ? (démarques OU exclusions)
   * ⚠️ C'est ce qui allume le bandeau ambre et le bouton « Mettre à jour » : sans lui, un gérant
   * croirait ses coches appliquées aux caisses alors qu'elles ne sont qu'affichées (§23.0).
   */
  const dirty = useMemo(() => {
    if (!dejaCetteSaison || campagne === null) return false;
    const memesMarks = marks !== null && marks.length === campagne.marks.length
      && marks.every((m, i) => m === campagne.marks[i]!.pct);
    const cle = (x: ExclusionSoldes) => `${x.dim}:${x.value}`;
    const a = [...exclusions.map(cle)].sort().join('|');
    const b = [...campagne.exclusions.map(cle)].sort().join('|');
    return !memesMarks || a !== b;
  }, [dejaCetteSaison, campagne, marks, exclusions]);

  // ⚠️ HOOKS AVANT TOUT RETOUR ANTICIPÉ (échec, chargement) : appelés après, ils ne le seraient pas
  //    à chaque rendu — React exige le même ordre, et la règle le refuse à juste titre.
  const pctsAffiches = marks ?? MARKS_DEFAUT;
  // Les colonnes suivent le nombre de démarques réglé — jamais une liste figée.
  const colonnesApercu = useMemo(() => colonnesDeLApercu(pctsAffiches), [pctsAffiches]);

  /** Le compte de la pastille « Exclus des soldes » — les TROIS familles réunies. */
  const totalExclus = etat === null ? 0
    : etat.comptes.exclusNonRemisables + etat.comptes.exclusPrixLibre + etat.comptes.exclusCampagne;

  async function activer() {
    if (!marks || !fenetre || !etat) return;
    const ok = await confirm({
      title: dejaCetteSaison ? t('Mettre à jour les démarques ?') : t('Activer les {v} ?', { v: fenetre.label.toLowerCase() }),
      message: t('{demarques} ({taux}) deviennent des promotions descendues aux caisses — {eligibles}. Les articles marqués « non remisable » sur leur fiche restent au prix fort.', {
        demarques: t('{n} démarque', { n: marks.length }),
        taux: marks.map((m) => `−${m} %`).join(', '),
        eligibles: t('{n} article éligible', { n: etat.comptes.eligibles }),
      }),
      confirmLabel: dejaCetteSaison ? t('Mettre à jour') : t('Activer'),
    });
    if (!ok) return;
    setEcriture(true);
    try {
      // ⚠️ Le jeu COMPLET d'exclusions part à chaque écriture (le serveur diffe et retire ce qui
      //    manque) — en omettre une l'effacerait des caisses sans que personne ne l'ait demandé.
      const r = await api.soldesActivate({ season, marks, exclusions });
      const retirees = r.retirees > 0 ? ` · ${t('{n} exclusion retirée', { n: r.retirees })}` : '';
      const eteintes = r.eteintes > 0 ? ` · ${t('{n} ancienne éteinte', { n: r.eteintes })}` : '';
      showToast(t('{ecrites}{eteintes}{retirees} — les caisses les recevront au prochain contact', {
        ecrites: t('{n} démarque écrite', { n: r.promotions }), eteintes, retirees,
      }), 'ok');
      // L'édition rejoint l'écrit : ce que le serveur relit devient la nouvelle référence.
      setExclEdit(null);
      setEtat(await api.soldesState(season, marks));
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setEcriture(false);
    }
  }

  async function eteindre() {
    const ok = await confirm({
      title: t('Éteindre les soldes ?'),
      // La phrase de la maquette — vraie par mécanique : une pièce scellée ne se recalcule pas.
      message: t('Les ventes déjà réalisées gardent leur prix — un prix appliqué ne se recalcule jamais. Seules les ventes à venir cessent d’être démarquées.'),
      confirmLabel: t('Éteindre'), tone: 'danger',
    });
    if (!ok) return;
    setEcriture(true);
    try {
      const r = await api.soldesOff();
      showToast(t('{n} démarque éteinte', { n: r.eteintes }), 'ok');
      setEtat(await api.soldesState(season, marks ?? MARKS_DEFAUT));
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setEcriture(false);
    }
  }

  /** Pose l'arrêté de la saison affichée — la campagne, elle, ne bouge qu'au clic « Activer ». */
  async function enregistrerDates() {
    if (!fenetre) return;
    setEcriture(true);
    try {
      await api.soldesDates({ season, year: fenetre.year, start: datesBrutes.start, end: datesBrutes.end });
      setEtat(await api.soldesState(season, marks ?? MARKS_DEFAUT));
      setEditionDates(false);
      showToast(t('Dates d’arrêté enregistrées — la règle nationale reprendra l’année suivante.'), 'ok');
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setEcriture(false);
    }
  }

  async function revenirALaRegle() {
    setEcriture(true);
    try {
      await api.soldesDates({ season, clear: true });
      setEtat(await api.soldesState(season, marks ?? MARKS_DEFAUT));
      setEditionDates(false);
      showToast(t('Arrêté retiré — la règle nationale s’applique.'), 'ok');
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setEcriture(false);
    }
  }

  if (failure !== null) {
    return <p role="alert" style={{ fontSize: 'var(--t-lead)', color: 'var(--danger-ink)' }}>{t('Les soldes n’ont pas pu être lus : {failure}', { failure })}</p>;
  }
  if (etat === null || fenetre === null) {
    return <p style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture…')}</p>;
  }


  return (
    <div>
      {/* ── Campagne d'une autre saison restée allumée (ajout fonctionnel, hors maquette) ── */}
      {campagne !== null && campagne.active && !dejaCetteSaison && (
        <div style={{
          marginBottom: 16, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)',
          border: '1px solid var(--amber-fg)', fontSize: 'var(--t-body)', color: 'var(--ink-800)',
        }}>
          
          <Trans k='Une campagne <1>{saison} {annee}</1> est encore active — activer celle-ci l’éteindra dans le même geste.'
            vars={{ saison: campagne.saison === 'hiver' ? t('d’hiver') : t('d’été'), annee: campagne.year }}
            parts={[(x) => <strong>{x}</strong>]} />
        </div>
      )}

      {/* ── Les deux saisons : label + règle. Les dates vivent dans le panneau, comme la maquette. ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 11, marginBottom: 16, alignItems: 'stretch' }}>
        {etat.saisons.map((f) => (
          <button aria-pressed={season === f.saison}
            key={f.saison} type="button" onClick={() => setSeason(f.saison)}
            style={{
              display: 'flex', flexDirection: 'column', gap: 5, padding: '15px 16px', textAlign: 'left',
              height: '100%', cursor: 'pointer', borderRadius: 'var(--r-sm)',
              border: `1.5px solid ${season === f.saison ? 'var(--teal-500)' : 'var(--ink-200)'}`,
              background: season === f.saison ? 'var(--teal-100)' : 'var(--paper)',
            }}
          >
            <span style={{ display: 'block', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{f.label}</span>
            <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.45 }}>{f.rule}</span>
          </button>
        ))}
      </div>

      {/* ── Le panneau de la saison : dates imposées + démarques + note légale ── */}
      <div style={{ ...CARTE, padding: '17px 18px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <span style={{ flex: 1, minWidth: 160 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{fenetre.label}</span>
            <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', marginTop: 2 }}>
              {day(fenetre.startMs)} → {day(fenetre.endMs)}
            </span>
          </span>
          {/* La pastille dit d'OÙ viennent les dates : la règle calculée, ou un arrêté saisi ici. */}
          {fenetre.arretee ? (
            <span style={{ ...PASTILLE_GRISE, background: 'var(--amber-bg)', color: 'var(--amber-fg)' }}>{t('Dates fixées par arrêté')}</span>
          ) : (
            <span style={PASTILLE_GRISE}>{t('Dates imposées')}</span>
          )}
          <button
            type="button" className="btn-ghost" disabled={ecriture}
            onClick={() => {
              if (!editionDates) setDatesBrutes({ start: jourCivilParis(fenetre.startMs), end: jourCivilParis(fenetre.endMs) });
              setEditionDates(!editionDates);
            }}
          >
            {editionDates ? t('Fermer') : t('Corriger')}
          </button>
        </div>

        {/* ── L'arrêté : le remplacement de la règle quand un décret ou une dérogation déplace les dates ── */}
        {editionDates && (
          <div style={{ marginBottom: 14, padding: '13px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              {([['start', t('Du')], ['end', t('Au (inclus)')]] as const).map(([champ, libelle]) => (
                <label key={champ} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={MONO_LABEL}>{libelle}</span>
                  <input
                    type="date" value={datesBrutes[champ]}
                    onChange={(e) => setDatesBrutes({ ...datesBrutes, [champ]: e.target.value })}
                    style={{
                      height: 36, padding: '0 10px', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
                      border: '1px solid var(--ink-200)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)',
                      fontFamily: 'var(--font-mono)', outline: 'none',
                    }}
                  />
                </label>
              ))}
              <button
                type="button" disabled={ecriture || !datesBrutes.start || !datesBrutes.end}
                onClick={() => { void enregistrerDates(); }}
                style={{
                  height: 36, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
                  background: 'var(--ink-900)', color: 'var(--paper)', fontSize: 'var(--t-body)', fontWeight: 700,
                  cursor: 'pointer', opacity: ecriture || !datesBrutes.start || !datesBrutes.end ? 0.5 : 1,
                }}
              >{t('Enregistrer l’arrêté')}</button>
              {fenetre.arretee && (
                <button type="button" className="btn-ghost" disabled={ecriture} onClick={() => { void revenirALaRegle(); }}>
                  
                  {t('Revenir à la règle')}
                </button>
              )}
            </div>
            <p style={{ margin: '10px 0 0', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
              <Trans k='Pour un report par décret ou une dérogation préfectorale (frontaliers, outre-mer). L’arrêté ne vaut que pour <1>{year}</1> — la règle nationale reprend seule l’année suivante. Si la campagne est déjà activée, « Mettre à jour » réécrit les mêmes démarques aux nouvelles dates.' vars={{ year: fenetre.year }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></p>
          </div>
        )}

        {/* Un arrêté posé APRÈS l'activation : les promotions écrites portent les anciennes dates. */}
        {etat.desync && (
          <div role="alert" style={{
            marginBottom: 14, padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)',
            border: '1px solid var(--amber-fg)', fontSize: 'var(--t-body)', color: 'var(--ink-800)',
          }}>
            <Trans k='Les dates ont changé <1>après l’activation</1> : la campagne en caisse porte encore les anciennes. « Mettre à jour les démarques » réécrit les mêmes promotions aux nouvelles dates.' parts={[(x) => <strong>{x}</strong>]} /></div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 11 }}>
          {marksBruts.map((brut, i) => {
            const lisible = Number.isInteger(Number.parseInt(brut.trim(), 10));
            const depart = etat.repartition[i]?.fromMs ?? null;
            return (
              <div key={i} style={{ padding: '13px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
                <span style={{ display: 'block', ...MONO_LABEL }}>{t('{v} démarque', { v: ordinal(i) })}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
                  <input
                    aria-label={t('{v} démarque', { v: ordinal(i) })} inputMode="numeric" value={brut}
                    onChange={(e) => setMarksBruts(marksBruts.map((v, x) => (x === i ? e.target.value : v)))}
                    style={{
                      height: 36, padding: '0 10px', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
                      border: `1px solid ${lisible ? 'var(--ink-200)' : 'var(--rose)'}`,
                      color: 'var(--ink-900)', fontSize: 'var(--t-lead)', outline: 'none', width: 66,
                      fontFamily: 'var(--font-mono)', textAlign: 'right',
                    }}
                  />
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-700)' }}>%</span>
                </span>
                <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: 7 }}>
                  {depart != null ? t('à partir du {v}', { v: day(depart) }) : '—'}
                </span>
              </div>
            );
          })}
        </div>
        {marks === null && (
          <p style={{ margin: '10px 0 0', fontSize: 'var(--t-dense)', color: 'var(--rose-fg)' }}>
            
            {t('Les démarques sont des pourcentages entiers (1–90), strictement croissants — une 2ᵉ démarque moins forte que la 1ʳᵉ remonterait les prix en pleins soldes.')}
          </p>
        )}

        <div style={NOTE_GRISE}>
          <Trans k='Les <1>dates ne se choisissent pas</1> : la règle nationale les calcule, et « Corriger » les remplace <2>quand un arrêté les déplace</2> (report par décret, dérogation frontalière ou outre-mer). Le <3>prix de référence barré</3> est le prix le plus bas pratiqué dans les <4>30 jours précédant</4> le début des soldes — calculé depuis l’historique de prix, jamais saisi.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
      </div>

      {/* ── Exclusions de campagne (maquette 2026-08-30) ── */}
      <CarteExclusions
        exclusions={exclusions}
        onChange={(next) => setExclEdit(next)}
        campN={etat.comptes.exclusCampagne}
        dirty={dirty}
        ecriture={ecriture}
        onUpdate={() => { void activer(); }}
        failure={failure}
        onRetry={() => setRelance((n) => n + 1)}
      />

      {/* ── Aperçu des démarques — tableau du KIT (une seule mécanique dans tout le BO, §6.4-impl-c) ── */}
      <DataTable<ApercuSolde>
        tableId="bo-soldes-apercu"
        unit={{ one: t('article'), gender: 'm' }}
        columns={colonnesApercu}
        rows={etat.apercu}
        getRowId={(r) => r.code}
        status={{ phase: 'ready' }}
        emptyStates={{
          empty: {
            icon: 'tag',
            title: t('Aucun article soldable'),
            // ⚠️ Le motif dépend des exclusions posées : dire « aucun article » tout court laisserait
            //    chercher une panne là où le périmètre a simplement été réduit à néant (§23.0).
            note: exclusions.length > 0
              ? t('Vos exclusions écartent tout ce qui était soldable. Retirez-en une pour retrouver un périmètre.')
              : t('Aucun article actif à prix positif et remisable dans ce magasin.'),
          },
        }}
        toolbar={
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
            {exclusions.length > 0 ? t('{n} modèle éligible — exclusions déduites', { n: etat.comptes.eligibles }) : t('{n} modèle éligible', { n: etat.comptes.eligibles })}
          </span>
        }
      />
      {/* ── Exclus des soldes — TROIS familles de motifs, jamais confondues (maquette 2026-08-30) ── */}
      <div style={{ ...CARTE, padding: '17px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <span style={{ flex: 1, minWidth: 160, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Exclus des soldes')}</span>
          <span style={{ ...PASTILLE_GRISE, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {t('{n} article', { n: totalExclus })}
          </span>
        </div>
        <DataTable<SoldesExcludedRow>
          tableId="bo-soldes-exclus"
          unit={{ one: t('article'), gender: 'm' }}
          columns={colonnesExclus()}
          rows={etat.comptes.exclusRows}
          getRowId={(r) => `${r.kind}:${r.code}`}
          status={{ phase: 'ready' }}
          emptyStates={{
            empty: {
              icon: 'check',
              title: t('Aucun article exclu'),
              note: t('Tout le catalogue soldable sera démarqué.'),
            },
          }}
        />
        <div style={NOTE_GRISE}>
          <Trans k='Trois familles de motifs, jamais confondues : l’<1>exclusion de campagne</1> (vos choix ci-dessus — un article qui y correspond n’est jamais démarqué, même visé par ailleurs), le drapeau <2>non remisable</2> de la fiche (il interdit toute remise, pas seulement les soldes) et le <3>prix libre</3>.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
      </div>

      {/* ── La barre d'activation : trois états, jamais deux à la fois (maquette 2026-08-30) ── */}
      <div style={{ ...CARTE, padding: '15px 18px', marginTop: 16, display: 'flex', alignItems: 'center', gap: 13, flexWrap: 'wrap' }}>
        {!dejaCetteSaison ? (
          <>
            <span style={{ flex: 1, minWidth: 220, fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
              <Trans k='Les démarques ne sont <1>pas encore aux caisses</1>. L’activation matérialise saison, démarques et exclusions en promotions descendues à tout le magasin.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></span>
            <button type="button" disabled={ecriture || marks === null}
              title={marks === null ? t('Corrigez les démarques avant d’activer.') : undefined}
              onClick={() => { void activer(); }}
              style={{
                height: 40, padding: '0 17px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)',
                color: '#fff', fontSize: 'var(--t-title)', fontWeight: 700, boxShadow: '0 6px 18px -8px var(--teal-500)',
                whiteSpace: 'nowrap', cursor: 'pointer', opacity: ecriture || marks === null ? 0.6 : 1,
              }}>
              {ecriture ? t('Écriture…') : t('Activer les {v}', { v: fenetre.label.toLowerCase() })}
            </button>
          </>
        ) : dirty ? (
          <>
            <span style={{ flex: 1, minWidth: 220, fontSize: 'var(--t-body)', color: 'var(--ink-800)', lineHeight: 1.5 }}>
              <Trans k='<1>Campagne active</1> — vos changements ne sont pas encore aux caisses.' parts={[(x) => <strong>{x}</strong>]} /></span>
            <button type="button" disabled={ecriture || marks === null} onClick={() => { void activer(); }}
              style={{
                height: 40, padding: '0 17px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--teal-500)',
                color: '#fff', fontSize: 'var(--t-title)', fontWeight: 700, boxShadow: '0 6px 18px -8px var(--teal-500)',
                whiteSpace: 'nowrap', cursor: 'pointer', opacity: ecriture || marks === null ? 0.6 : 1,
              }}>
              {ecriture ? t('Écriture…') : t('Mettre à jour les démarques')}
            </button>
          </>
        ) : (
          <>
            <span style={{
              height: 26, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '0 11px',
              borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)',
              fontSize: 'var(--t-dense)', fontWeight: 700,
            }}>
              <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)' }} />
              
              {t('Campagne active aux caisses')}
            </span>
            <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
              
              {t('À jour — toute modification des démarques ou des exclusions proposera une réécriture.')}
            </span>
          </>
        )}
        {campagne !== null && campagne.active && (
          <button type="button" className="btn-ghost" disabled={ecriture} onClick={() => { void eteindre(); }}>
            
            {t('Éteindre les soldes')}
          </button>
        )}
      </div>
    </div>
  );
}
